
import { Component, Prop, Vue } from "vue-property-decorator";

/** The target every month is measured against. */
const TARGET = 100;
/** Brand blue, dark enough to clear 3:1 on the page in light and dark. */
const BAR_COLOUR = "#1f7ab8";
const INK_MUTED = "#8b8b8b";
const GRID = "#e8e8e8";

@Component
export default class GoalsChart extends Vue {
  @Prop()
  header?: string;

  @Prop({ required: true, default: () => [] })
  categories!: Array<string | string[]>;

  @Prop({ required: true, default: () => [] })
  dataSeries!: Array<number | string>;

  /**
   * Computed, not assigned once in a class field. They used to be built from
   * the props at construction, so the chart kept whatever it was given first
   * and never redrew when another user was picked.
   */
  get points(): number[] {
    return (this.dataSeries || []).map(
      (value: number | string) => Number(value) || 0
    );
  }

  get hasData(): boolean {
    return this.points.length > 0;
  }

  get series() {
    return [{ name: "Progress", data: this.points }];
  }

  get labels(): string[] {
    return (this.categories || []).map((category: string | string[]) =>
      Array.isArray(category) ? category[0] : category
    );
  }

  /** Headroom above the tallest bar, so a month over target is not clipped. */
  get axisMax(): number {
    return Math.max(TARGET, ...this.points) * 1.1;
  }

  get chartOptions() {
    return {
      chart: {
        type: "bar",
        height: 350,
        fontFamily: "inherit",
        toolbar: { show: false },
        animations: { enabled: false }
      },
      // One series, so no legend: the heading already names it.
      legend: { show: false },
      colors: [BAR_COLOUR],
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          columnWidth: "55%",
          dataLabels: { position: "top" }
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (value: number) => `${Math.round(value)}%`,
        offsetY: -18,
        style: { fontSize: "11px", colors: [INK_MUTED] }
      },
      states: { hover: { filter: { type: "darken", value: 0.9 } } },
      xaxis: {
        categories: this.labels,
        axisBorder: { color: GRID },
        axisTicks: { show: false },
        labels: { style: { colors: INK_MUTED, fontSize: "11px" } }
      },
      yaxis: {
        min: 0,
        max: this.axisMax,
        labels: {
          formatter: (value: number) => `${Math.round(value)}%`,
          style: { colors: INK_MUTED, fontSize: "11px" }
        }
      },
      grid: {
        borderColor: GRID,
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        padding: { top: 8 }
      },
      // The target itself, so a bar can be read against something.
      annotations: {
        yaxis: [
          {
            y: TARGET,
            borderColor: INK_MUTED,
            strokeDashArray: 4,
            label: {
              text: "Target",
              position: "left",
              textAnchor: "start",
              borderWidth: 0,
              style: {
                background: "transparent",
                color: INK_MUTED,
                fontSize: "11px"
              }
            }
          }
        ]
      },
      tooltip: {
        y: { formatter: (value: number) => `${value.toFixed(2)}% of target` }
      }
    };
  }
}
